import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LinearProgress, ListItemIcon } from '@material-ui/core'

import { MoreHoriz } from '@material-ui/icons'
import { Select, SelectProps } from 'components/inputs/Select/Select'
import { Label } from 'types/Label'
import { useGetLabelsQuery } from 'api/queries/labels/labels'
import { ADMIN_ROLE } from 'constants/roleGroups'
import { useHasRole } from 'hooks/useHasRoles'
import { useCompanyId } from 'providers/CompanyProvider'
import { LabelChip } from 'components/data/LabelChip/LabelChip'
import * as Styled from './LabelSelect.styles'

interface LabelSelectProps extends SelectProps {
  className?: string
  onlyCompany?: boolean
  initialData?: Label[]
  inputLabel?: string
}

const LabelSelect: React.FC<LabelSelectProps> = ({
  className,
  onlyCompany,
  initialData = [],
  inputLabel,
  ...rest
}) => {
  const { t } = useTranslation()
  const isAdmin = useHasRole(ADMIN_ROLE)
  const companyId = useCompanyId() || 0

  const {
    data: labelsData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useGetLabelsQuery(isAdmin ? { companyId } : undefined)

  const handleClickLoadMore = (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>
  ) => {
    e.stopPropagation()
    if (hasNextPage) {
      fetchNextPage()
    }
  }

  const isAnythingLoading = isLoading
    || isFetching
    || isFetchingNextPage

  const labelsDataContent = useMemo(
    () => labelsData?.pages?.flatMap((page) => page.content) || [],
    [labelsData?.pages]
  )

  const labels = useMemo(() => {
    const seen = new Set<number>()

    return [...labelsDataContent, ...initialData].filter(
      (label) => {
        if (seen.has(label.id)) return false
        seen.add(label.id)
        return true
      }
    )
  }, [initialData, labelsDataContent])

  return (
    <Select
      label={inputLabel}
      className={className}
      displayEmpty
      renderValue={(selected) =>
        ((selected as number[]).length === 0
          ? (<span>{t('labels.noneSelected')}</span>)
          : (selected as number[]).map((value) => {
            const label = labels.find(({ id }) => id === value)
            return (
              <LabelChip
                key={value}
                color={label?.color || '#000000'}
                name={label?.name || ''}
                nameTranslations={label?.nameTranslations || []}
                inSelect
                small
                clickable
              />
            )
          }))}
      {...rest}
    >
      {labels.length === 0 && !isAnythingLoading && (
        <Styled.MenuItem disabled onClick={(e) => e.stopPropagation()}>
          {t('labels.empty')}
        </Styled.MenuItem>
      )}
      {labels.map(({ id, name, color, nameTranslations }) => (
        <Styled.MenuItem key={id} value={id}>
          <LabelChip
            color={color}
            name={name}
            nameTranslations={nameTranslations}
            small
            clickable
          />
        </Styled.MenuItem>
      ))}
      {isAnythingLoading && <LinearProgress />}
      {!isAnythingLoading && (hasNextPage) && (
      <Styled.MenuItem onClick={handleClickLoadMore}>
        <ListItemIcon>
          <MoreHoriz />
        </ListItemIcon>
      </Styled.MenuItem>
      )}
    </Select>
  )
}

export { LabelSelect }
